/**
 * Shared entry path for starting a topic-scoped DISCOVERY session.
 *
 * The two-step "persist selection → navigate into DISCOVERY" sequence is used
 * from DiscoverySection's "Explore this topic" panel button.
 *
 * Factoring it here keeps every caller on one definition of "enter topic
 * discovery" so they can't drift.
 *
 * @module core/topicDiscoveryEntry
 */

import { eventBus } from './utils.js';
import { DiscoveryAPI } from './discoveryApi.js';

/**
 * Persist the guided topic selection, then navigate into a topic-scoped
 * DISCOVERY session. Selection-persist failure is logged but non-fatal — the
 * navigation still fires so the user lands in discovery (the loop re-resolves
 * the topic from the navigate event).
 *
 * @param {string} topicId - Topic registry id (e.g. 'connection')
 * @param {Object} [opts]
 * @param {DiscoveryAPI} [opts.discoveryAPI] - Inject an existing instance
 *   (DiscoverySection passes its own); defaults to a fresh one.
 * @returns {Promise<void>}
 */
export async function enterTopicDiscovery(topicId, { discoveryAPI } = {}) {
  if (!topicId) {
    console.error('[topicDiscoveryEntry] enterTopicDiscovery called without topicId');
    return;
  }

  const api = discoveryAPI || new DiscoveryAPI();
  try {
    await api.selectTopic(topicId);
  } catch (err) {
    console.error('[topicDiscoveryEntry] Failed to persist topic selection:', err);
  }

  eventBus.emit('navigate', {
    page: 'chat',
    mode: 'DISCOVERY',
    topicId
  });
}
